import * as React from "react";
import * as Immutable from "immutable";
import { useContext, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import useProductName from "brand-customization/useProductName";
import { InputOptionalInfo as Opt, FormikFormGroup, Select } from "components/common";
import { Button, ButtonToolbar, Alert, Row, Col, Input } from "components/bootstrap";
import BackendWizardContext from "components/authentication/directoryServices/BackendWizard/BackendWizardContext";
import { validateField, formHasErrors } from "util/FormsUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import MatchingGroupsOverview from "./MatchingGroupsOverview";
import SelectedGroupsOverview from "./SelectedGroupsOverview";
import SelectionTypeOptions from "./SelectionTypeOptions";
import GroupSyncCheckbox from "./GroupSyncCheckbox";
import LicenseCheck from "../../../license/LicenseCheck";
const Headline = styled.h3`
  margin-bottom: 20px;
`;
export const formValidation = (selectionType) => {
  let teamSelection;
  if (selectionType !== "all") {
    teamSelection = {
      required: true
    };
  }
  const validation = {
    groupSearchBase: {
      required: true
    },
    groupSearchPattern: {
      required: true
    },
    teamNameAttribute: {
      required: true
    },
    teamUniqueIdAttribute: {
      required: true
    },
    teamDefaultRoles: {},
    teamSelectionType: {},
    teamSelection
  };
  return validation;
};
const _getInitialValues = (formValues) => {
  const {
    groupSearchPattern,
    groupSearchBase,
    teamUniqueIdAttribute,
    teamNameAttribute,
    teamDefaultRoles,
    teamSelectionType,
    teamSelection,
    synchronizeGroups
  } = formValues;
  return {
    synchronizeGroups,
    groupSearchPattern,
    groupSearchBase,
    teamUniqueIdAttribute,
    teamNameAttribute,
    teamDefaultRoles,
    teamSelectionType: teamSelectionType || "all",
    teamSelection: teamSelection || Immutable.Set()
  };
};
const GroupSyncForm = ({
  help = {},
  prepareSubmitPayload,
  formRef,
  onSubmitAll,
  submitAllError,
  validateOnMount,
  roles,
  excludedFields
}) => {
  const { t } = useTranslation("enterprise_authentication");
  const { setStepsState, ...stepsState } = useContext(BackendWizardContext);
  const { backendValidationErrors } = stepsState;
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id })).toArray();
  const _formValidation = useCallback((teamSelectionType) => formValidation(teamSelectionType), []);
  const sendTelemetry = useSendTelemetry("services-groupsync-step");
  const productName = useProductName();
  const _onSubmitAll = (validateForm, values, licenseIsValid) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_GROUP_SYNC_SAVE_CLICKED, {
      app_action_value: "save-service"
    });
    if (values.synchronizeGroups) {
      return validateForm().then((errors) => {
        if (!formHasErrors(errors)) {
          onSubmitAll(licenseIsValid);
        }
      });
    }
    return onSubmitAll(licenseIsValid);
  };
  return /* @__PURE__ */ React.createElement(LicenseCheck, null, ({ licenseIsValid }) => {
    const disableGroupSyncStep = !licenseIsValid;
    return /* @__PURE__ */ React.createElement(
      Formik,
      {
        initialValues: _getInitialValues(stepsState.formValues),
        onSubmit: () => onSubmitAll(licenseIsValid),
        innerRef: formRef,
        validateOnBlur: false,
        validateOnChange: false,
        validateOnMount
      },
      ({ isSubmitting, values, setFieldValue, validateForm }) => {
        const validation = _formValidation(values.teamSelectionType);
        const disableForm = disableGroupSyncStep || !values.synchronizeGroups;
        const _onGroupSelect = (groupId) => {
          var _a;
          const selectedGroupsIds = (_a = values.teamSelection) != null ? _a : Immutable.List();
          if (selectedGroupsIds == null ? void 0 : selectedGroupsIds.includes(groupId)) {
            setFieldValue("teamSelection", selectedGroupsIds.remove(groupId));
          } else {
            setFieldValue("teamSelection", selectedGroupsIds.add(groupId));
          }
        };
        return /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { xs: 12 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info", title: t("directory_services.group_sync_form.introduction_title") }, /* @__PURE__ */ React.createElement(
          Trans,
          {
            t,
            i18nKey: "directory_services.group_sync_form.introduction",
            values: { serviceTitle: stepsState.authBackendMeta.serviceTitle, productName },
            components: { i: /* @__PURE__ */ React.createElement("i", null) }
          }
        )))), /* @__PURE__ */ React.createElement(GroupSyncCheckbox, { disabled: disableGroupSyncStep }), /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.groupSearchBase,
            validate: validateField(validation.groupSearchBase),
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.groupSearchBase,
            label: t("directory_services.group_sync_form.group_search_base_dn"),
            disabled: disableForm,
            name: "groupSearchBase",
            placeholder: t("directory_services.group_sync_form.group_search_base")
          }
        ), /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.groupSearchPattern,
            validate: validateField(validation.groupSearchPattern),
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.groupSearchPattern,
            disabled: disableForm,
            label: t("directory_services.group_sync_form.group_search_pattern"),
            name: "groupSearchPattern",
            placeholder: t("directory_services.group_sync_form.group_search_pattern")
          }
        ), /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.teamNameAttribute,
            validate: validateField(validation.teamNameAttribute),
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.teamNameAttribute,
            disabled: disableForm,
            label: t("directory_services.group_sync_form.team_name_attribute"),
            name: "teamNameAttribute",
            placeholder: t("directory_services.group_sync_form.team_name_attribute")
          }
        ), !excludedFields.teamUniqueIdAttribute && /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.teamUniqueIdAttribute,
            label: t("directory_services.group_sync_form.team_id_attribute"),
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.teamUniqueIdAttribute,
            validate: validateField(validation.teamUniqueIdAttribute),
            disabled: disableForm,
            name: "teamUniqueIdAttribute",
            placeholder: t("directory_services.group_sync_form.team_id_attribute")
          }
        ), /* @__PURE__ */ React.createElement(Field, { name: "teamDefaultRoles", validate: validateField(validation.teamDefaultRoles) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            help: help.teamDefaultRoles,
            error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.teamDefaultRoles,
            id: "default-team-roles-select",
            disabled: disableForm,
            label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("directory_services.group_sync_form.default_team_roles"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              multi: true,
              onBlur,
              onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
              options: rolesOptions,
              disabled: disableForm,
              placeholder: t("directory_services.group_sync_form.search_for_roles"),
              value
            }
          )
        )), /* @__PURE__ */ React.createElement(Headline, null, t("directory_services.group_sync_form.load_matching_groups")), /* @__PURE__ */ React.createElement(
          MatchingGroupsOverview,
          {
            disabled: disableForm,
            prepareSubmitPayload,
            onGroupSelect: _onGroupSelect
          }
        ), /* @__PURE__ */ React.createElement(Headline, null, t("directory_services.group_sync_form.select_groups_to_import")), /* @__PURE__ */ React.createElement(SelectionTypeOptions, { disabled: disableForm }), /* @__PURE__ */ React.createElement(SelectedGroupsOverview, { onGroupSelect: _onGroupSelect, validation }), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
          Button,
          {
            bsStyle: "primary",
            disabled: isSubmitting,
            onClick: () => _onSubmitAll(validateForm, values, licenseIsValid)
          },
          t("directory_services.group_sync_form.finish_and_save")
        )));
      }
    );
  });
};
export default GroupSyncForm;
